import { zipCodeToState } from '@shared';
import {
  AdvisorInfoWithWritingCode,
  formatDateForForm,
  mapGender,
  mapMaritalStatus,
  returnSafeString,
} from './carrier-utils';

export const SECURIAN_PREFILL_SECTIONS_PRIMARY = [
  'proposed-insured-information',
  'ltc-income-questions',
  'ltc-savings-investments-disclosure',
];

export const getClientUpdatesForSecurian = (clientInfo: Client): PreFillUpdate[] => {
  const clientUpdates = [
    {
      questionRef: 'proposedInsuredName',
      answer: `${clientInfo.clientLastName} ${clientInfo.clientFirstName}`,
    },
    {
      questionRef: 'proposedInsuredEmail',
      answer: clientInfo.clientEmail,
      sourceQuestionRefs: ['clientEmail'],
    },
    {
      questionRef: 'ownerEmail',
      answer: clientInfo.clientEmail,
      sourceQuestionRefs: ['clientEmail'],
    },
    {
      questionRef: 'repReportProposedInsuredName',
      answer: `${clientInfo.clientFirstName} ${clientInfo.clientLastName}`,
    },
    {
      questionRef: 'repReportOwnerName',
      answer: `${clientInfo.clientFirstName} ${clientInfo.clientLastName}`,
    },
    {
      questionRef: 'goodHealthApplicantName',
      answer: `${clientInfo.clientFirstName} ${clientInfo.clientLastName}`,
    },
    {
      questionRef: 'policy1InsuredFirstName',
      answer: `${clientInfo.clientFirstName}`,
    },
    {
      questionRef: 'policy1InsuredLastName',
      answer: `${clientInfo.clientLastName}`,
    },
  ].filter(update => update.answer);

  const directIntakeMappings = getDirectIntakeMappings(clientInfo);

  return [...clientUpdates, ...directIntakeMappings];
};

const getDirectIntakeMappings = (clientInfo: Client): PreFillUpdate[] => {
  if (clientInfo?.intakeSurvey) {
    const { intakeSurvey } = clientInfo;

    const clientMappings = [
      {
        questionRef: 'proposedInsuredSex',
        answer: mapGender(intakeSurvey.clientGenderAtBirth),
        sourceQuestionRefs: ['clientGenderAtBirth'],
      },
      {
        questionRef: 'proposedInsuredDob',
        answer: formatDateForForm(intakeSurvey?.clientBirthDate),
        sourceQuestionRefs: ['clientBirthDate'],
      },
      {
        questionRef: 'proposedInsuredMaritalStatus',
        answer: mapMaritalStatus(intakeSurvey.clientMaritalStatus, 'securian'),
        sourceQuestionRefs: ['clientMaritalStatus'],
      },
      {
        questionRef: 'proposedInsuredZip',
        answer: intakeSurvey?.clientZipCode,
        sourceQuestionRefs: ['clientZipCode'],
      },
      {
        questionRef: 'proposedInsuredState',
        answer: zipCodeToState(intakeSurvey?.clientZipCode).stateAbbreviation || '',
        sourceQuestionRefs: ['clientZipCode'],
      },
      {
        questionRef: 'ownerDobOrTrustDate',
        answer: formatDateForForm(intakeSurvey?.clientBirthDate),
        sourceQuestionRefs: ['clientBirthDate'],
      },
      {
        questionRef: 'ownerZip',
        answer: intakeSurvey?.clientZipCode,
        sourceQuestionRefs: ['clientZipCode'],
      },
      {
        questionRef: 'ownerState',
        answer: zipCodeToState(intakeSurvey?.clientZipCode).stateAbbreviation || '',
        sourceQuestionRefs: ['clientZipCode'],
      },
      {
        questionRef: 'proposedInsuredEarnedIncome',
        answer: returnSafeString(intakeSurvey.householdPriorYearIncome),
        sourceQuestionRefs: ['householdPriorYearIncome'],
      },
      {
        questionRef: 'proposedInsuredHouseholdIncome',
        answer: returnSafeString(intakeSurvey.householdPriorYearIncome),
        sourceQuestionRefs: ['householdPriorYearIncome'],
      },
      {
        questionRef: 'proposedInsuredTotalNetWorth',
        answer: returnSafeString(intakeSurvey.householdTotalAssetsValue),
        sourceQuestionRefs: ['householdTotalAssetsValue'],
      },
      {
        questionRef: 'householdAnnualIncome',
        answer:
          typeof intakeSurvey.householdPriorYearIncome === 'number'
            ? intakeSurvey.householdPriorYearIncome < 50000
              ? 'under-50k'
              : intakeSurvey.householdPriorYearIncome < 75000
                ? '50k-75k'
                : intakeSurvey.householdPriorYearIncome < 100000
                  ? '75k-100k'
                  : intakeSurvey.householdPriorYearIncome <= 250000
                    ? '100k-250k'
                    : intakeSurvey.householdPriorYearIncome <= 500000
                      ? '250k-500k'
                      : 'over-500k'
            : '',
        sourceQuestionRefs: ['householdPriorYearIncome'],
      },
      {
        questionRef: 'savingsInvestmentsWorth',
        answer:
          typeof intakeSurvey.householdTotalAssetsValue === 'number'
            ? intakeSurvey.householdTotalAssetsValue < 50000
              ? 'under-50k'
              : intakeSurvey.householdTotalAssetsValue < 75000
                ? '50k-75k'
                : intakeSurvey.householdTotalAssetsValue < 100000
                  ? '75k-100k'
                  : intakeSurvey.householdTotalAssetsValue <= 250000
                    ? '100k-250k'
                    : intakeSurvey.householdTotalAssetsValue <= 500000
                      ? '250k-500k'
                      : 'over-500k'
            : '',
        sourceQuestionRefs: ['householdTotalAssetsValue'],
      },
      {
        questionRef: 'inForceCoverageQ1',
        answer: 'no',
      },
    ].filter(
      update => update.answer && (Array.isArray(update.answer) ? update.answer.length > 0 : true),
    );
    return clientMappings;
  }
  return [];
};

export const getPolicyUpdatesForSecurian = (
  policyFundingSource: PolicyFundingSource,
): PreFillUpdate[] => {
  let initialQuote = policyFundingSource.updatedQuote || policyFundingSource.sourceQuote;
  if (typeof initialQuote === 'string') {
    try {
      initialQuote = JSON.parse(initialQuote);
    } catch (e) {
      console.error('Failed to parse quote:', e);
      initialQuote = null;
    }
  }

  const isPolicyQuote = (quote: any): quote is PolicyQuote => {
    return quote && typeof quote === 'object' && 'premiumAmount' in quote;
  };

  const sourceQuote = isPolicyQuote(initialQuote) ? initialQuote : null;

  const policyUpdates = [
    {
      questionRef: 'policyInsuranceAmount',
      answer: returnSafeString(sourceQuote?.deathBenefitInitial),
    },
    {
      questionRef: 'policyBenefitDuration',
      answer: sourceQuote?.ltcBenefitDurationMonths
        ? `${sourceQuote.ltcBenefitDurationMonths / 12}-year`
        : '',
    },
    {
      questionRef: 'policyInflationProtection',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? 'reject-all'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('3') &&
              sourceQuote.inflationBenefitOption.toLowerCase().includes('simple')
            ? '3-simple'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('3') &&
                sourceQuote.inflationBenefitOption.toLowerCase().includes('compound')
              ? '3-compound'
              : sourceQuote.inflationBenefitOption.toLowerCase().includes('5') &&
                  sourceQuote.inflationBenefitOption.toLowerCase().includes('simple')
                ? '5-simple'
                : sourceQuote.inflationBenefitOption.toLowerCase().includes('5') &&
                    sourceQuote.inflationBenefitOption.toLowerCase().includes('compound')
                  ? '5-compound'
                  : 'reject-all'
        : 'reject-all',
    },
    {
      questionRef: 'premiumMethod',
      answer: sourceQuote?.productFundingOption
        ? sourceQuote.productFundingOption.toLowerCase().includes('single premium')
          ? 'single-premium'
          : sourceQuote.productFundingOption.toLowerCase().includes('annual') &&
              sourceQuote.productFundingOption.toLowerCase().includes('additional')
            ? 'annual-premium-plus-first-year'
            : sourceQuote.productFundingOption.toLowerCase().includes('annual')
              ? 'annual-premium'
              : ''
        : '',
    },
    {
      questionRef: 'premiumAnnualAmount',
      answer: sourceQuote?.productFundingOption.toLowerCase().includes('annual')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'premiumSingleAmount',
      answer: sourceQuote?.productFundingOption.toLowerCase().includes('single premium')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'premiumPaymentFrequency',
      answer: sourceQuote?.productFundingOption.toLowerCase().includes('annual') ? 'annual' : '',
    },
    {
      questionRef: 'ltcPremiumMethod',
      answer: sourceQuote?.productFundingOption
        ? sourceQuote.productFundingOption.toLowerCase().includes('single premium')
          ? 'single-premium'
          : sourceQuote.productFundingOption.toLowerCase().includes('annual') &&
              sourceQuote.productFundingOption.toLowerCase().includes('additional')
            ? 'annual-premium-with-first-year'
            : sourceQuote.productFundingOption.toLowerCase().includes('annual')
              ? 'annual-premium'
              : ''
        : '',
    },
    {
      questionRef: 'ltcAnnualPremiumAmount',
      answer: sourceQuote?.productFundingOption.toLowerCase().includes('annual')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'incomeBuyInflationProtection',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? 'no'
          : 'yes'
        : '',
    },
    {
      questionRef: 'requestedTotalBenefitPool',
      answer: returnSafeString(sourceQuote?.ltcBenefitTotal),
    },
    {
      questionRef: 'riderRequestedTotalLtcBenefit',
      answer: returnSafeString(sourceQuote?.ltcBenefitTotal),
    },
    {
      questionRef: 'riderRequestedTotalDeathBenefit',
      answer: returnSafeString(sourceQuote?.deathBenefitInitial),
    },
    {
      questionRef: 'requestedOriginalBaseMonthlyBenefit',
      answer: returnSafeString(sourceQuote?.ltcBenefitMonthlyIndividual),
    },
    {
      questionRef: 'riderRequestedMonthlyBenefit',
      answer: returnSafeString(sourceQuote?.ltcBenefitMonthlyIndividual),
    },
    {
      questionRef: 'requestedYearsLtcCoverage',
      answer: returnSafeString(sourceQuote?.ltcBenefitDurationMonths ?? 0 / 12),
    },
    {
      questionRef: 'riderRequestedNumOfLtcYears',
      answer: returnSafeString(sourceQuote?.ltcBenefitDurationMonths ?? 0 / 12),
    },
    {
      questionRef: 'requestedInflationBenefitType',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? 'none'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('simple')
            ? 'simple'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('compound')
              ? 'compound'
              : 'none'
        : 'none',
    },
    {
      questionRef: 'requestedSimpleInflationPercent',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? '0'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('3')
            ? '3'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('5')
              ? '5'
              : '0'
        : '0',
    },
    {
      questionRef: 'requestedCompoundInflationPercent',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? '0'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('3')
            ? '3'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('5')
              ? '5'
              : '0'
        : '0',
    },
    {
      questionRef: 'riderRequestedInflationBenefitType',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? 'none'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('simple')
            ? 'simple'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('compound')
              ? 'compound'
              : 'none'
        : 'none',
    },
    {
      questionRef: 'riderRequestedSimpleInflationPercent',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? '0'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('3')
            ? '3'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('5')
              ? '5'
              : '0'
        : '0',
    },
    {
      questionRef: 'riderRequestedCompoundInflationPercent',
      answer: sourceQuote?.inflationBenefitOption
        ? sourceQuote.inflationBenefitOption.toLowerCase().includes('no inflation')
          ? '0'
          : sourceQuote.inflationBenefitOption.toLowerCase().includes('3')
            ? '3'
            : sourceQuote.inflationBenefitOption.toLowerCase().includes('5')
              ? '5'
              : '0'
        : '0',
    },
    {
      questionRef: 'requestedPremiumType',
      answer: sourceQuote?.productFundingOption
        ? sourceQuote.productFundingOption.toLowerCase().includes('single premium')
          ? 'single-premium'
          : sourceQuote.productFundingOption.toLowerCase().includes('annual')
            ? 'annual-premium'
            : ''
        : '',
    },
    {
      questionRef: 'requestedPremiumAnnualAmount',
      answer: sourceQuote?.productFundingOption.toLowerCase().includes('annual')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'requestedPremiumSingleAmount',
      answer: sourceQuote?.productFundingOption.toLowerCase().includes('single premium')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'riderRequestedPremiumType',
      answer: sourceQuote?.productFundingOption
        ? sourceQuote.productFundingOption.toLowerCase().includes('single premium')
          ? 'single-premium'
          : sourceQuote.productFundingOption.toLowerCase().includes('annual')
            ? 'annual-premium'
            : ''
        : '',
    },
    {
      questionRef: 'riderRequestedPremiumAnnualAmount',
      answer: sourceQuote?.productFundingOption.toLowerCase().includes('annual')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'riderRequestedPremiumSingleAmount',
      answer: sourceQuote?.productFundingOption.toLowerCase().includes('single premium')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'riderRequestedYearsPremiumPayment',
      answer: sourceQuote?.productFundingOption
        ? sourceQuote.productFundingOption.toLowerCase().includes('single premium')
          ? 'single'
          : ''
        : '',
    },
    {
      questionRef: 'requestedYearsPremiumPayment',
      answer: sourceQuote?.productFundingOption
        ? sourceQuote.productFundingOption.toLowerCase().includes('single premium')
          ? 'single'
          : ''
        : '',
    },
  ].filter(update => update.answer);

  return policyUpdates;
};

export const getAdvisorUpdatesForSecurian = (
  advisorInfo: AdvisorInfoWithWritingCode,
): PreFillUpdate[] => {
  return [
    {
      questionRef: 'licensedFpName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'licensedFpFirmCode',
      answer: advisorInfo.bgaWritingCode || '',
    },
    {
      questionRef: 'firmRepCode',
      answer: advisorInfo.bgaWritingCode || '',
    },
    {
      questionRef: 'repReportServicingRepFirmCode',
      answer: advisorInfo.bgaWritingCode || '',
    },
    {
      questionRef: 'edcLicensedRepresentativeName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'edcLicensedRepresentativeEmail',
      answer: advisorInfo.advisorEmail || '',
    },
  ].filter(update => update.answer);
};
